/** Batch "Also book onto" — fan-out targets across many mappings.
 *
 * Deliberately shaped like Manage tags (add / replace / remove over a picked
 * set) because it is the same mental move, but the stakes are higher: a target
 * is not a label, it is a tour that receives its own booking every time the
 * mapping matches. Adding one retroactively changes what future bookings do.
 *
 * Targets are tenant-scoped, so the dialog requires a single tenant. Unlike
 * Manage tags there is no cross-tenant strategy: there is no sensible "create
 * the twin" for a tour, so a mixed-tenant selection is refused up front rather
 * than half-applied.
 */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import * as syncApi from '@/api/sync';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

type TargetOp = 'add' | 'replace' | 'remove';

interface TourOpt {
  id: number;
  name: string;
}

interface Props {
  ctx: BatchActionContext;
  /** The grid's tenant when one is picked — then no picker is needed. */
  tenant: TenantPickerValue | null;
}

export function ManageTargetsBatchDialog({ ctx, tenant: gridTenant }: Props) {
  const [tenant, setTenant] = useState<TenantPickerValue | null>(gridTenant);
  const [op, setOp] = useState<TargetOp>('add');
  const [tours, setTours] = useState<TourOpt[]>([]);
  const [busy, setBusy] = useState(false);

  const searchTours = useMemo(
    () => async (q: string, signal: AbortSignal) =>
      tenant ? await syncApi.searchTours(tenant.id, q, signal) : [],
    [tenant],
  );

  // `remove` with an empty list is meaningless here (unlike tags, where it
  // means "the whole category"), so every op needs at least one tour.
  const canSubmit = Boolean(tenant) && tours.length > 0 && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      await ctx.runStandard({ op, tour_ids: tours.map((t) => t.id) });
      ctx.close();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Manage fan-out tours</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Applies to <strong>{ctx.matchedPhrase}</strong>.
          </Typography>

          <Alert severity="warning">
            Every tour listed here receives <strong>its own booking</strong> each time one of the
            selected mappings matches. This is not a label.
          </Alert>

          {!gridTenant && (
            <>
              <TenantPicker value={tenant} onChange={setTenant} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Tours are tenant-scoped, so pick the tenant whose tours you mean. Mappings from
                other tenants in the selection are reported as skipped rather than silently
                cross-booked.
              </Typography>
            </>
          )}

          <RadioGroup row value={op} onChange={(e) => setOp(e.target.value as TargetOp)}>
            <FormControlLabel value="add" control={<Radio />} label="Add" />
            <FormControlLabel value="replace" control={<Radio />} label="Replace" />
            <FormControlLabel value="remove" control={<Radio />} label="Remove" />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {op === 'add' && 'Keeps existing fan-out tours and adds these.'}
            {op === 'replace' && 'Discards every existing fan-out tour, then adds these.'}
            {op === 'remove' && 'Removes these tours; any others are left alone.'}
          </Typography>

          <RemoteAutocomplete<TourOpt>
            label="Tours"
            multiple
            queryFn={searchTours}
            getOptionLabel={(o) => o.name}
            getOptionKey={(o) => o.id}
            value={tours}
            onChange={(v) => setTours((v as TourOpt[]) ?? [])}
            placeholder={tenant ? 'Search tours…' : 'Pick a tenant first'}
            disabled={!tenant || busy}
            fetchOnEmpty
            size="small"
          />

          <Alert severity="info">
            A mapping is never fanned out to the tour it already points at — that would double-book
            it — and only additional mappings can fan out at all.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={op === 'replace' ? 'warning' : 'primary'}
          onClick={() => void submit()}
          disabled={!canSubmit}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
